/**
 * Suspense-enabled hook for fetching explore categories
 * Suspends during loading - use with <Suspense fallback={...}>
 */
import { useSuspenseQuery } from '@tanstack/react-query';
import { isPublished } from '../Utils/isPublished';
import { getAllExploreCategoriesV3, resolveCategoryId } from '../Model/Explore/exploreCategoryTreeV3';

export type ExploreCategoryType = 'movement' | 'mental' | 'nutrition' | 'academy';

export function useExploreCategoriesSuspense(categoryType: ExploreCategoryType) {
  const { data } = useSuspenseQuery({
    queryKey: ['exploreCategoriesV3', 'children', categoryType],
    queryFn: async () => {
      // V3 returns a flat category set and has no slug lookup, so resolve the
      // root identifier (e.g. 'mental') to its UUID and return its direct
      // children (the cards shown on the tab landing).
      const all = await getAllExploreCategoriesV3();
      const parentId = resolveCategoryId(all, categoryType);
      if (!parentId) return [];
      return all.filter((c) => (c.parentIds ?? []).includes(parentId)).filter(isPublished);
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  const categories = data ?? [];

  return { content: categories, categories };
}
